import React, { createRef, useEffect, useState, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";
import Styles from "./SignIn.module.css";
import visibleImg from "../../Assets/visible.png";
import unvisible from "../../Assets/unvisible.png";
import { loginService } from "../../Services/ApiServices";
import { setCookie } from "../../Utility/Utility";
import { useAuth } from "../../Services/Context";
import gif from "../../Assets/kstore.gif";
import VaniGoLogo from "../../Assets/vanigo_logo.png";
import {
  requestNotificationPermission,
  registerMessagingServiceWorker,
} from "../../firebase-config";

// Live production reCAPTCHA key for kansaswallet.in
const LIVE_RECAPTCHA_KEY = "6LdVfbQUAAAAAPyAQBi_ZMs0dyj97jJhJ_GpuP6R";

// Google official test key (enables testing on localhost, local IP, and Cloudflare/ngrok tunnels without "Invalid domain" error)
const DEV_RECAPTCHA_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

// Separate dev and live: use production key on kansaswallet.in, dev key on testing environments
const isProduction =
  typeof window !== "undefined" &&
  (window.location.hostname === "kansaswallet.in" ||
    window.location.hostname.endsWith(".kansaswallet.in") ||
    process.env.REACT_APP_ENV === "production");

const sitekey = isProduction ? LIVE_RECAPTCHA_KEY : DEV_RECAPTCHA_KEY;

// Helpers for encoding/decoding remembered credentials safely
const encodePass = (pass) => {
  try {
    return btoa(unescape(encodeURIComponent(pass)));
  } catch (e) {
    try {
      return btoa(pass);
    } catch (err) {
      return pass;
    }
  }
};

const decodePass = (encoded) => {
  try {
    return decodeURIComponent(escape(atob(encoded)));
  } catch (e) {
    try {
      return atob(encoded);
    } catch (err) {
      return "";
    }
  }
};

const getSavedCredentials = () => {
  try {
    if (typeof window !== "undefined") {
      const isRemembered =
        localStorage.getItem("kstore_remember_me") === "true";
      const savedUser =
        localStorage.getItem("kstore_remember_username") || "";
      const savedPassRaw =
        localStorage.getItem("kstore_remember_password") || "";
      if (isRemembered && savedUser) {
        return {
          email: savedUser,
          password: savedPassRaw ? decodePass(savedPassRaw) : "",
          rememberMe: true,
        };
      }
    }
  } catch (e) {
    console.warn("Error reading saved credentials:", e);
  }
  return { email: "", password: "", rememberMe: false };
};

function SignIn() {
  let navigate = useNavigate();
  const recaptchaRef = createRef();
  const { handleAuthLogin } = useAuth();
  const [visible, setVisible] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [error, setError] = useState("");
  const [notificationPerm, setNotificationPerm] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "default"
  );
  const savedCreds = useMemo(() => getSavedCredentials(), []);
  const [rememberMe, setRememberMe] = useState(savedCreds.rememberMe);

  const { handleChange, handleSubmit, values, errors, touched, setFieldValue } =
    useFormik({
      initialValues: {
        email: savedCreds.email,
        password: savedCreds.password,
        recaptcha: "",
      },
      validationSchema: Yup.object({
        email: Yup.string().required("Required"),
        password: Yup.string().required("Required"),
        recaptcha: Yup.string().required("Captcha is required").nullable(),
      }),
      onSubmit: (values) => {
        handleLogin(values);
      },
    });

  useEffect(() => {
    // 1. Pre-register messaging SW in background so it is active before user submits
    registerMessagingServiceWorker();

    // 2. Pre-fetch token into localStorage if already granted
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "granted" &&
      !localStorage.getItem("kstore_fcm_token")
    ) {
      requestNotificationPermission().catch(() => {});
    }

    // 3. Ensure saved credentials are auto-filled
    if (savedCreds.rememberMe && savedCreds.email) {
      setFieldValue("email", savedCreds.email);
      if (savedCreds.password) {
        setFieldValue("password", savedCreds.password);
      }
    }
  }, [savedCreds, setFieldValue]);

  const handleLogin = async (data) => {
    setIsLoader(true);
    let formData = new FormData();
    formData.append("username", data.email);
    formData.append("password", data.password);

    let pushId = localStorage.getItem("kstore_fcm_token");
    if (!pushId && typeof window !== "undefined" && "Notification" in window) {
      try {
        pushId = await requestNotificationPermission();
        if ("Notification" in window) {
          setNotificationPerm(Notification.permission);
        }
      } catch (err) {
        console.warn("Could not get push_id on login:", err);
      }
    }

    formData.append("push_id", pushId || "");

    loginService(formData)
      .then((res) => {
        if (rememberMe) {
          localStorage.setItem("kstore_remember_username", data.email);
          localStorage.setItem(
            "kstore_remember_password",
            encodePass(data.password)
          );
          localStorage.setItem("kstore_remember_me", "true");
        } else {
          localStorage.removeItem("kstore_remember_username");
          localStorage.removeItem("kstore_remember_password");
          localStorage.removeItem("kstore_remember_me");
        }
        handleAuthLogin(res.data);
        setCookie("userlog", JSON.stringify(res.data), 30);
        navigate("/store");
      })
      .catch((err) => {
        if (err?.response?.data?.detail) {
          toast(err.response.data.detail[0].msg, { type: "error" });
          setError(err?.response?.data?.detail[0]?.attempts);
        } else {
          toast("Something went wrong!!", { type: "error" });
        }
      })
      .finally(() => setIsLoader(false));
  };

  const logoTapCountRef = useRef(0);
  const logoTapTimerRef = useRef(null);

  const handleLogoTap = () => {
    logoTapCountRef.current += 1;
    clearTimeout(logoTapTimerRef.current);
    logoTapTimerRef.current = setTimeout(() => {
      logoTapCountRef.current = 0;
    }, 2000);

    if (logoTapCountRef.current >= 5) {
      logoTapCountRef.current = 0;
      const isEnabled = localStorage.getItem("debug_console") === "true";
      if (isEnabled) {
        localStorage.removeItem("debug_console");
        toast("Dev Console Disabled", { icon: "ℹ️" });
        if (window.eruda) window.eruda.destroy();
      } else {
        localStorage.setItem("debug_console", "true");
        toast.success("Dev Console Enabled!");
        if (window.eruda) {
          window.eruda.init();
        } else {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/npm/eruda";
          s.onload = () => {
            if (window.eruda) window.eruda.init();
          };
          document.head.appendChild(s);
        }
      }
    }
  };

  function onChange(value) {
    setFieldValue("recaptcha", value);
  }

  return (
    <>
      <img
        className={Styles.logo}
        src={VaniGoLogo}
        alt="vanigo"
        onClick={handleLogoTap}
        style={{ cursor: "pointer" }}
        title="Tap 5 times to toggle Dev Console"
      />
      <div className={Styles.overallParent}>
        {isLoader ? (
          <div className={Styles.loaderParent}>
            <div className={Styles.loader}></div>
          </div>
        ) : null}
        <div className={Styles.loginRightContainer}>
          <div>
            <h3 className="kstoreFont">Login</h3>
            {notificationPerm === "default" && (
              <div className={Styles.notifBanner}>
                <div className={Styles.notifText}>
                  🔔 <strong>Enable Notifications</strong> for live order alerts.
                </div>
                <button
                  type="button"
                  className={Styles.notifBtn}
                  onClick={async () => {
                    try {
                      const token = await requestNotificationPermission();
                      if ("Notification" in window) {
                        setNotificationPerm(Notification.permission);
                      }
                      if (token) {
                        toast.success("Order notifications enabled!");
                      }
                    } catch (e) {
                      console.warn("Enable notif err:", e);
                    }
                  }}
                >
                  Allow
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mt-4">
                <label className="label">Username</label>
                <div className={Styles.inputContainer}>
                  <input
                    className={Styles.emailInput + " contentFont"}
                    type="text"
                    name="email"
                    autoComplete="new-password"
                    onChange={handleChange}
                    value={values.email}
                    placeholder="Enter Username"
                  />
                </div>
              </div>
              <p className={Styles.error}>{error}</p>
              {touched.email && errors.email ? (
                <div className={Styles.errorMsg + " text-danger"}>
                  {errors.email}
                </div>
              ) : null}
              <div className="mt-4">
                <label className="label">Password</label>
                <div className={Styles.inputContainer}>
                  <input
                    name="password"
                    onChange={handleChange}
                    autoComplete="new-password"
                    value={values.password}
                    className={Styles.passwordInput + " contentFont"}
                    placeholder="Enter Password"
                    type={visible ? "text" : "password"}
                  />

                  <img
                    onClick={() => setVisible(!visible)}
                    className={Styles.visibleImg}
                    src={visible ? visibleImg : unvisible}
                  />
                </div>
              </div>
              {touched.password && errors.password ? (
                <div className={Styles.errorMsg + " text-danger"}>
                  {errors.password}
                </div>
              ) : null}

              <div className={Styles.rememberMeContainer}>
                <label className={Styles.rememberMeLabel}>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    name="rememberMe"
                    className={Styles.rememberMeCheckbox}
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className={Styles.rememberMeText + " contentFont"}>
                    Remember Me
                  </span>
                </label>
              </div>

              <div className="mt-4">
                <div className={Styles.recaptchaWrapper}>
                  <form
                    onSubmit={() => {
                      recaptchaRef.current.execute();
                    }}
                    className="gRecaptcha d-flex justify-content-center"
                  >
                    <ReCAPTCHA
                      onErrored
                      ref={recaptchaRef}
                      sitekey={sitekey}
                      onChange={onChange}
                    />
                  </form>
                </div>
                {errors?.recaptcha ? (
                  <div className={Styles.errorMsg + " text-danger text-center mt-2"}>
                    {errors.recaptcha}
                  </div>
                ) : null}
              </div>

              <button
                type="submit"
                className={Styles.loginBtn + " mt-4 kstoreFont"}
              >
                LOGIN
              </button>
            </form>
          </div>
          <div className={Styles.footerLinksContainer}>
            <Link
              className={Styles.forgotPassword + " kstoreFont"}
              to="/auth/forgotpassword"
            >
              Forgot Password
            </Link>
            <Link
              className={Styles.backToHomeLink + " kstoreFont"}
              to="/home"
            >
              Back to Home →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default SignIn;
